import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchCoursesForLeads, leadApi, leadSetupApi } from "../../pages/services/leadService";
import { fetchCourseById } from "../../pages/services/courseService";
import TemplateMessageField from "./TemplateMessageField";
import { errorText, fmtDateTime, fromLocalInput, toLocalInput, useLeadMeta } from "./leadUtils";
import "../../styles/Leads.css";

const TABS = [
  { key: "campaigns", label: "Campaigns" },
  { key: "automation", label: "Course automation" },
  { key: "demos", label: "Demo sessions" },
];

const Modal = ({ title, onClose, children }) => (
  <div className="ld-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className="ld-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ld-modal-head"><h3>{title}</h3><button type="button" className="ld-btn ld-btn-sm" onClick={onClose}>Close</button></div>
      <div className="ld-modal-body">{children}</div>
    </div>
  </div>
);

// ═══ Campaigns ═══════════════════════════════════════════════════════════════
const CampaignsTab = ({ courses }) => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => leadSetupApi.campaigns().then(setRows).catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing._id) await leadSetupApi.updateCampaign(editing._id, editing);
      else await leadSetupApi.createCampaign(editing);
      setEditing(null);
      load();
    } catch (err) { setError(errorText(err)); }
  };
  const set = (k) => (e) => setEditing((c) => ({ ...c, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <>
      <div className="ld-section-head">
        <p className="ld-sub" style={{ margin: 0 }}>Every lead belongs to a campaign. A website enquiry goes to its course's <b>website campaign</b>, created automatically the first time.</p>
        <button type="button" className="ld-btn ld-btn-primary" onClick={() => setEditing({ name: "", courseId: "", source: "website", description: "", isActive: true, isWebsiteDefault: false })}>+ New campaign</button>
      </div>
      {error && !editing && <div className="ld-alert ld-alert-error">{error}</div>}
      <div className="ld-card">
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Campaign</th><th>Course</th><th>Source</th><th>Leads</th><th>Registered</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c._id} onClick={() => setEditing({ ...c })}>
                  <td><div className="ld-name">{c.name}</div>{c.isWebsiteDefault && <div className="ld-sub">Receives website leads</div>}</td>
                  <td>{c.courseTitle}</td>
                  <td className="ld-sub">{c.source}</td>
                  <td>{c.leads}</td>
                  <td>{c.registered}</td>
                  <td className="ld-sub">{c.isActive ? "Active" : "Inactive"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="ld-empty">No campaigns yet. The first website enquiry for a course creates one.</div>}
        </div>
      </div>
      {editing && (
        <Modal title={editing._id ? "Edit campaign" : "New campaign"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            {error && <div className="ld-alert ld-alert-error">{error}</div>}
            <label className="ld-field"><span>Name<em>*</em></span><input className="ld-input" value={editing.name} onChange={set("name")} required /></label>
            <div className="ld-grid-2">
              <label className="ld-field"><span>Course<em>*</em></span>
                <select className="ld-select" value={editing.courseId || ""} onChange={set("courseId")} required disabled={editing._id && editing.leads > 0}>
                  <option value="">Select a course…</option>
                  {courses.map((c) => <option key={c._id} value={c._id}>{c.courseTitle}</option>)}
                </select>
              </label>
              <label className="ld-field"><span>Source</span><input className="ld-input" value={editing.source} onChange={set("source")} placeholder="website, ads, referral, walk-in…" /></label>
            </div>
            <label className="ld-field"><span>Description</span><textarea className="ld-textarea" value={editing.description} onChange={set("description")} /></label>
            <label className="ld-toggle" style={{ marginBottom: 10 }}><input type="checkbox" checked={!!editing.isWebsiteDefault} onChange={set("isWebsiteDefault")} /><span className="ld-toggle-track" /> Website enquiries for this course go to this campaign</label><br />
            <label className="ld-toggle"><input type="checkbox" checked={!!editing.isActive} onChange={set("isActive")} /><span className="ld-toggle-track" /> Active</label>
            <div className="ld-modal-foot" style={{ padding: "16px 0 0", borderTop: "none" }}>
              {editing._id && !editing.leads && <button type="button" className="ld-btn ld-btn-danger" style={{ marginRight: "auto" }} onClick={async () => { if (window.confirm("Delete this campaign?")) { await leadSetupApi.deleteCampaign(editing._id).catch((e) => setError(errorText(e))); setEditing(null); load(); } }}>Delete</button>}
              <button type="button" className="ld-btn" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="ld-btn ld-btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

// ═══ Course automation ═══════════════════════════════════════════════════════
const DEMO_PRESETS = [
  { title: "36 hours before", mode: "offset", offsetMinutes: -36 * 60 },
  { title: "24 hours before", mode: "offset", offsetMinutes: -24 * 60 },
  { title: "Same day, 9 AM", mode: "sameDayAt", sameDayAt: "09:00" },
  { title: "1 hour before", mode: "offset", offsetMinutes: -60 },
  { title: "15 minutes before — only 15 minutes remaining", mode: "offset", offsetMinutes: -15 },
  { title: "Class started", mode: "offset", offsetMinutes: 0 },
  { title: "Class started already — 10 minutes elapsed", mode: "offset", offsetMinutes: 10 },
];
const CUSTOM_PRESETS = [
  "It was nice talking to you — here are the details",
  "Course brochure (PDF)",
  "Payment plan for the course",
  "Not reachable / no response",
  "No response — 2nd time: interest confirmation",
  "No response after the 3rd time: closure",
];
const STATUS_MESSAGES = [
  { key: "created", label: "Acknowledgement", hint: "Sent the moment a lead arrives (WhatsApp and email)." },
  { key: "ready_for_demo", label: "Demo details", hint: "Sent when a lead is set to Ready for Demo and given a session." },
  { key: "registered", label: "Course details & fee structure", hint: "Sent when the lead is Registered (registration fee received)." },
];

// Minutes before/after the demo, edited as a number + unit + before/after.
const OffsetInput = ({ minutes, onChange }) => {
  const abs = Math.abs(minutes || 0);
  const hours = abs >= 60 && abs % 60 === 0;
  const amount = hours ? abs / 60 : abs;
  const sign = (minutes || 0) < 0 ? -1 : 1;
  const emit = (a, h, s) => onChange(Math.round((Number(a) || 0) * (h ? 60 : 1)) * s);
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <input className="ld-input ld-input-sm" style={{ width: 80 }} type="number" min="0" value={amount} onChange={(e) => emit(e.target.value, hours, sign)} />
      <select className="ld-select" style={{ width: "auto" }} value={hours ? "h" : "m"} onChange={(e) => emit(amount, e.target.value === "h", sign)}>
        <option value="m">minutes</option><option value="h">hours</option>
      </select>
      <select className="ld-select" style={{ width: "auto" }} value={sign < 0 ? "before" : "after"} onChange={(e) => emit(amount, hours, e.target.value === "before" ? -1 : 1)}>
        <option value="before">before the start</option><option value="after">after the start</option>
      </select>
    </span>
  );
};

const AutomationTab = ({ courses, sources }) => {
  const [params, setParams] = useSearchParams();
  const courseId = params.get("course") || "";
  // The templates on the UraanWapp account, per channel: { list, configured, error }.
  const [providers, setProviders] = useState({ whatsapp: null, email: null });
  const [auto, setAuto] = useState(null);
  const [droppedEmails, setDroppedEmails] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const course = courses.find((c) => c._id === courseId);

  const loadTemplates = useCallback((fresh = false) => {
    for (const channel of ["whatsapp", "email"]) {
      leadSetupApi.providerTemplates(channel, { fresh })
        .then((r) => setProviders((p) => ({ ...p, [channel]: { list: r.data || [], configured: r.configured !== false, error: r.error || "" } })))
        .catch((e) => setProviders((p) => ({ ...p, [channel]: { list: [], configured: true, error: errorText(e) } })));
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  // The course's brochure and fee structure (course page), for the link parameters and attachments.
  const [courseFiles, setCourseFiles] = useState(null);
  useEffect(() => {
    setCourseFiles(null);
    if (courseId) fetchCourseById(courseId).then((c) => setCourseFiles({ courseBrochure: c?.courseBrochure, feeStructureBrochure: c?.feeStructureBrochure, fileLinks: c?.fileLinks || {} })).catch(() => {});
  }, [courseId]);
  useEffect(() => {
    setAuto(null);
    setDroppedEmails(0);
    if (courseId) leadSetupApi.automation(courseId).then((d) => { setAuto(d.automation); setDroppedEmails(d.droppedEmails || 0); }).catch((e) => setError(errorText(e)));
  }, [courseId]);

  const patch = (fn) => setAuto((a) => { const copy = JSON.parse(JSON.stringify(a)); fn(copy); return copy; });
  const save = async () => {
    setSaving(true); setError(""); setNotice("");
    try {
      const saved = await leadSetupApi.saveAutomation(courseId, auto);
      setAuto(saved);
      setDroppedEmails(0);
      setNotice("Saved. Messages not yet sent will use these settings.");
    } catch (e) { setError(errorText(e)); } finally { setSaving(false); }
  };
  const field = (channel) => (props) => <TemplateMessageField channel={channel} templates={providers[channel]?.list || []} sources={sources} courseTitle={course?.courseTitle} courseFiles={courseFiles} {...props} />;
  const wa = field("whatsapp");
  const em = field("email");
  const providerProblem = ["whatsapp", "email"].map((c) => {
    const p = providers[c];
    const name = c === "email" ? "email" : "WhatsApp";
    if (!p) return null;
    if (!p.configured) return `The UraanWapp API key (URAANWAPP_API_KEY) is not set on the server, so no ${name} templates can be listed or sent.`;
    if (p.error) return `Could not load the ${name} templates from UraanWapp: ${p.error}`;
    if (!p.list.length) return `The UraanWapp account has no ${name} templates yet.`;
    return null;
  }).filter(Boolean);

  return (
    <>
      <div className="ld-filters">
        <div className="ld-filter" style={{ minWidth: 320 }}>
          <span className="ld-filter-label">Course</span>
          <select className="ld-select" value={courseId} onChange={(e) => { const n = new URLSearchParams(params); n.set("course", e.target.value); setParams(n); }}>
            <option value="">Select a course…</option>
            {courses.map((c) => <option key={c._id} value={c._id}>{c.courseTitle}</option>)}
          </select>
        </div>
        <span className="ld-sub" style={{ paddingBottom: 10 }}>
          WhatsApp and email templates come from your UraanWapp account — create and approve them there.{" "}
          <button type="button" className="ld-btn ld-btn-sm" onClick={() => loadTemplates(true)}>Refresh templates</button>
        </span>
      </div>
      {providerProblem.map((m) => <div key={m} className="ld-alert ld-alert-error">{m}</div>)}
      {courseId && courseFiles && (
        <div className="ld-sub" style={{ marginBottom: 10 }}>
          Course files:{" "}
          {[["courseBrochure", "brochure", "brochure"], ["feeStructureBrochure", "feeStructure", "fee structure"]].map(([f, kind, name], i) => (
            <span key={f}>{i ? " · " : ""}{courseFiles[f]?.url ? <a href={courseFiles.fileLinks?.[kind] || courseFiles[f].url} target="_blank" rel="noreferrer">{name}</a> : <span style={{ color: "#b45309" }}>no {name}</span>}</span>
          ))}
          {" — "}<Link to={`/courses/${courseId}`}>upload on the course page</Link>
        </div>
      )}
      {droppedEmails > 0 && <div className="ld-alert ld-alert-info">{droppedEmails} email message{droppedEmails === 1 ? " used" : "s used"} an email template made in the admin panel, which is no longer used — choose a UraanWapp email template for {droppedEmails === 1 ? "it" : "them"} and save.</div>}
      {error && <div className="ld-alert ld-alert-error">{error}</div>}
      {notice && <div className="ld-alert ld-alert-ok">{notice}</div>}
      {!courseId && <div className="ld-card ld-empty">Choose a course to set up its lead messages.</div>}
      {courseId && !auto && <div className="ld-card ld-empty">Loading…</div>}
      {auto && (
        <>
          <div className="ld-card">
            <h3 className="ld-card-title">Status messages</h3>
            {STATUS_MESSAGES.map((s) => {
              const m = auto.statusMessages?.[s.key] || {};
              return (
                <div key={s.key} className="ld-step">
                  <div className="ld-step-head">
                    <b style={{ flex: 1 }}>{s.label}</b>
                    <label className="ld-toggle"><input type="checkbox" checked={m.enabled !== false} onChange={(e) => patch((a) => { a.statusMessages[s.key].enabled = e.target.checked; })} /><span className="ld-toggle-track" /> On</label>
                  </div>
                  <div className="ld-sub">{s.hint}</div>
                  <div className="ld-grid-2">
                    {wa({ value: m.whatsapp, onChange: (v) => patch((a) => { a.statusMessages[s.key].whatsapp = v; }) })}
                    {em({ value: m.email, onChange: (v) => patch((a) => { a.statusMessages[s.key].email = v; }) })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ld-card">
            <div className="ld-section-head">
              <h4>Day-wise messages</h4>
              <button type="button" className="ld-btn ld-btn-sm" onClick={() => patch((a) => { const last = a.dayWise[a.dayWise.length - 1]; a.dayWise.push({ title: "", day: last ? last.day + 1 : 2, time: "10:00", enabled: true, whatsapp: {}, email: {} }); })}>+ Add step</button>
            </div>
            <p className="ld-sub" style={{ marginTop: 0 }}>Day 1 is the day the lead arrives. Each step goes out on its day at its time (IST) while automation is on, and stops when the lead is Registered, Not interested or Not qualified. A Day 1 step whose time has already passed is sent straight away.</p>
            {auto.dayWise.map((s, i) => (
              <div key={s._id || `new-${i}`} className="ld-step">
                <div className="ld-step-head">
                  <span className="ld-sub">Day</span>
                  <input className="ld-input ld-input-sm" type="number" min="1" value={s.day} onChange={(e) => patch((a) => { a.dayWise[i].day = Number(e.target.value); })} />
                  <span className="ld-sub">at</span>
                  <input className="ld-input ld-input-sm" style={{ width: 120 }} type="time" value={s.time} onChange={(e) => patch((a) => { a.dayWise[i].time = e.target.value; })} />
                  <input className="ld-input" placeholder="Title, e.g. Course benefits" value={s.title} onChange={(e) => patch((a) => { a.dayWise[i].title = e.target.value; })} />
                  <label className="ld-toggle"><input type="checkbox" checked={s.enabled !== false} onChange={(e) => patch((a) => { a.dayWise[i].enabled = e.target.checked; })} /><span className="ld-toggle-track" /> On</label>
                  <button type="button" className="ld-btn ld-btn-sm ld-btn-danger" onClick={() => patch((a) => { a.dayWise.splice(i, 1); })}>Remove</button>
                </div>
                <div className="ld-grid-2">
                  {wa({ value: s.whatsapp, onChange: (v) => patch((a) => { a.dayWise[i].whatsapp = v; }) })}
                  {em({ value: s.email, onChange: (v) => patch((a) => { a.dayWise[i].email = v; }) })}
                </div>
              </div>
            ))}
            {!auto.dayWise.length && <div className="ld-empty">No day-wise steps.</div>}
          </div>

          <div className="ld-card">
            <div className="ld-section-head">
              <h4>Demo reminders</h4>
              <span style={{ display: "flex", gap: 8 }}>
                {!auto.demoReminders.length && <button type="button" className="ld-btn ld-btn-sm" onClick={() => patch((a) => { a.demoReminders = DEMO_PRESETS.map((p) => ({ ...p, enabled: true, whatsapp: {} })); })}>Add the standard 7</button>}
                <button type="button" className="ld-btn ld-btn-sm" onClick={() => patch((a) => { a.demoReminders.push({ title: "", mode: "offset", offsetMinutes: -60, sameDayAt: "09:00", enabled: true, whatsapp: {} }); })}>+ Add reminder</button>
              </span>
            </div>
            <p className="ld-sub" style={{ marginTop: 0 }}>Timed from the lead's demo session. Reminders already in the past when a lead is assigned (e.g. "36 hours before" for a demo tomorrow) are not sent.</p>
            {auto.demoReminders.map((r, i) => (
              <div key={r._id || `new-${i}`} className="ld-step">
                <div className="ld-step-head">
                  <input className="ld-input" placeholder="Title" value={r.title} onChange={(e) => patch((a) => { a.demoReminders[i].title = e.target.value; })} />
                  <select className="ld-select" style={{ width: "auto" }} value={r.mode} onChange={(e) => patch((a) => { a.demoReminders[i].mode = e.target.value; })}>
                    <option value="offset">Before / after the start</option>
                    <option value="sameDayAt">On the demo day at</option>
                  </select>
                  {r.mode === "offset"
                    ? <OffsetInput minutes={r.offsetMinutes} onChange={(m) => patch((a) => { a.demoReminders[i].offsetMinutes = m; })} />
                    : <input className="ld-input ld-input-sm" style={{ width: 120 }} type="time" value={r.sameDayAt} onChange={(e) => patch((a) => { a.demoReminders[i].sameDayAt = e.target.value; })} />}
                  <label className="ld-toggle"><input type="checkbox" checked={r.enabled !== false} onChange={(e) => patch((a) => { a.demoReminders[i].enabled = e.target.checked; })} /><span className="ld-toggle-track" /> On</label>
                  <button type="button" className="ld-btn ld-btn-sm ld-btn-danger" onClick={() => patch((a) => { a.demoReminders.splice(i, 1); })}>Remove</button>
                </div>
                {wa({ value: r.whatsapp, onChange: (v) => patch((a) => { a.demoReminders[i].whatsapp = v; }) })}
              </div>
            ))}
          </div>

          <div className="ld-card">
            <div className="ld-section-head">
              <h4>Custom messages</h4>
              <span style={{ display: "flex", gap: 8 }}>
                {!auto.customMessages.length && <button type="button" className="ld-btn ld-btn-sm" onClick={() => patch((a) => { a.customMessages = CUSTOM_PRESETS.map((title) => ({ title, whatsapp: {}, email: {} })); })}>Add the suggested 6</button>}
                <button type="button" className="ld-btn ld-btn-sm" onClick={() => patch((a) => { a.customMessages.push({ title: "", whatsapp: {}, email: {} }); })}>+ Add message</button>
              </span>
            </div>
            <p className="ld-sub" style={{ marginTop: 0 }}>Sent by hand — from a lead's page, or to many leads at once from the lead list (matched by title across courses). WhatsApp and/or email, no automation.</p>
            {auto.customMessages.map((m, i) => (
              <div key={m._id || `new-${i}`} className="ld-step">
                <div className="ld-step-head">
                  <input className="ld-input" placeholder="Title" value={m.title} onChange={(e) => patch((a) => { a.customMessages[i].title = e.target.value; })} />
                  <button type="button" className="ld-btn ld-btn-sm ld-btn-danger" onClick={() => patch((a) => { a.customMessages.splice(i, 1); })}>Remove</button>
                </div>
                <div className="ld-grid-2">
                  {wa({ value: m.whatsapp, onChange: (v) => patch((a) => { a.customMessages[i].whatsapp = v; }) })}
                  {em({ value: m.email, onChange: (v) => patch((a) => { a.customMessages[i].email = v; }) })}
                </div>
              </div>
            ))}
          </div>

          <div className="ld-card">
            <h3 className="ld-card-title">Payment link message</h3>
            <p className="ld-sub" style={{ marginTop: 0 }}>The WhatsApp message that carries a Razorpay payment link. Map one parameter to <b>Payment — link</b> (and the amount, if the template shows it).</p>
            {wa({ value: auto.paymentMessage, onChange: (v) => patch((a) => { a.paymentMessage = v; }) })}
          </div>

          <div className="ld-sticky-save">
            <button type="button" className="ld-btn ld-btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : `Save settings for ${course?.courseTitle || "course"}`}</button>
          </div>
        </>
      )}
    </>
  );
};

// ═══ Demo sessions ═══════════════════════════════════════════════════════════
const DemosTab = ({ courses }) => {
  const [rows, setRows] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [upcoming, setUpcoming] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const load = useCallback(() => leadSetupApi.demoSessions({ courseId, upcoming: upcoming ? "1" : "" }).then(setRows).catch((e) => setError(errorText(e))), [courseId, upcoming]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = { ...editing, startsAt: fromLocalInput(editing.startsAtLocal) };
      if (editing._id) {
        const r = await leadSetupApi.updateDemoSession(editing._id, body);
        setNotice(r.rescheduledLeads ? `Saved — reminders re-timed for ${r.rescheduledLeads} lead${r.rescheduledLeads === 1 ? "" : "s"}.` : "Saved.");
      } else {
        await leadSetupApi.createDemoSession(body);
        setNotice("Demo session added.");
      }
      setEditing(null);
      load();
    } catch (err) { setError(errorText(err)); }
  };

  return (
    <>
      <div className="ld-filters">
        <div className="ld-filter" style={{ minWidth: 280 }}>
          <span className="ld-filter-label">Course</span>
          <select className="ld-select" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">All courses</option>
            {courses.map((c) => <option key={c._id} value={c._id}>{c.courseTitle}</option>)}
          </select>
        </div>
        <label className="ld-toggle" style={{ paddingBottom: 10 }}><input type="checkbox" checked={upcoming} onChange={(e) => setUpcoming(e.target.checked)} /><span className="ld-toggle-track" /> Upcoming only</label>
        <button type="button" className="ld-btn ld-btn-primary" style={{ marginLeft: "auto" }} onClick={() => setEditing({ courseId, title: "", startsAtLocal: "", durationMinutes: 60, meetingLink: "", notes: "", isCancelled: false })}>+ New demo session</button>
      </div>
      {notice && <div className="ld-alert ld-alert-ok">{notice}</div>}
      {error && !editing && <div className="ld-alert ld-alert-error">{error}</div>}
      <div className="ld-card">
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>When</th><th>Course</th><th>Title</th><th>Leads</th><th>Link</th><th /></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s._id} onClick={() => setEditing({ ...s, startsAtLocal: toLocalInput(s.startsAt) })} style={s.isCancelled ? { opacity: 0.55 } : undefined}>
                  <td className="ld-name">{fmtDateTime(s.startsAt)}<div className="ld-sub">{s.durationMinutes} min</div></td>
                  <td>{s.courseTitle}</td>
                  <td>{s.title || "—"}{s.isCancelled && <div className="ld-field-error">Cancelled</div>}</td>
                  <td>{s.leads}</td>
                  <td className="ld-sub" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.meetingLink || "—"}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="ld-empty">No demo sessions{upcoming ? " coming up" : ""}.</div>}
        </div>
      </div>
      {editing && (
        <Modal title={editing._id ? "Edit demo session" : "New demo session"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            {error && <div className="ld-alert ld-alert-error">{error}</div>}
            {editing._id && editing.leads > 0 && <div className="ld-alert ld-alert-info">{editing.leads} lead{editing.leads === 1 ? " is" : "s are"} assigned. Changing the time or cancelling re-times their reminders.</div>}
            <div className="ld-grid-2">
              <label className="ld-field"><span>Course<em>*</em></span>
                <select className="ld-select" value={editing.courseId || ""} onChange={set("courseId")} required disabled={editing._id && editing.leads > 0}>
                  <option value="">Select a course…</option>
                  {courses.map((c) => <option key={c._id} value={c._id}>{c.courseTitle}</option>)}
                </select>
              </label>
              <label className="ld-field"><span>Title</span><input className="ld-input" value={editing.title} onChange={set("title")} placeholder="e.g. Saturday demo" /></label>
              <label className="ld-field"><span>Date & time<em>*</em></span><input type="datetime-local" className="ld-input" value={editing.startsAtLocal} onChange={set("startsAtLocal")} required /></label>
              <label className="ld-field"><span>Duration (minutes)</span><input type="number" min="15" className="ld-input" value={editing.durationMinutes} onChange={set("durationMinutes")} /></label>
            </div>
            <label className="ld-field"><span>Meeting link</span><input className="ld-input" value={editing.meetingLink} onChange={set("meetingLink")} placeholder="https://…" /></label>
            <label className="ld-field"><span>Notes</span><textarea className="ld-textarea" value={editing.notes} onChange={set("notes")} /></label>
            {editing._id && <label className="ld-toggle"><input type="checkbox" checked={!!editing.isCancelled} onChange={set("isCancelled")} /><span className="ld-toggle-track" /> Cancelled</label>}
            <div className="ld-modal-foot" style={{ padding: "16px 0 0", borderTop: "none" }}>
              {editing._id && !editing.leads && <button type="button" className="ld-btn ld-btn-danger" style={{ marginRight: "auto" }} onClick={async () => { if (window.confirm("Delete this session?")) { await leadSetupApi.deleteDemoSession(editing._id).catch((e) => setError(errorText(e))); setEditing(null); load(); } }}>Delete</button>}
              <button type="button" className="ld-btn" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="ld-btn ld-btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

// ═══ The page ════════════════════════════════════════════════════════════════
const LeadSetup = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "campaigns";
  const meta = useLeadMeta();
  const [courses, setCourses] = useState([]);
  const [running, setRunning] = useState("");
  useEffect(() => { fetchCoursesForLeads().then(setCourses).catch(() => {}); }, []);

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <Link to="/leads" className="ld-link">← Lead dashboard</Link>
          <h1 className="ld-title" style={{ marginTop: 6 }}>Lead settings</h1>
          <p className="ld-subtitle">Campaigns, the messages each course sends, and demo sessions.</p>
        </div>
        <div className="ld-head-actions">
          <button type="button" className="ld-btn" title="The scheduler runs every 5 minutes; this runs it now" onClick={async () => {
            setRunning("…");
            try { const r = await leadApi.runAutomation(); setRunning(r.busy ? "Another run is sending right now — try again in a minute" : `Sent ${r.sent}, failed ${r.failed}, skipped ${r.skipped}${r.retrying ? `, ${r.retrying} to try again (UraanWapp busy)` : ""}`); } catch (e) { setRunning(errorText(e)); }
          }}>Run automation now</button>
          {running && <span className="ld-sub">{running}</span>}
        </div>
      </div>
      <div className="ld-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={`ld-tab${tab === t.key ? " is-active" : ""}`} onClick={() => { const n = new URLSearchParams(params); n.set("tab", t.key); setParams(n); }}>{t.label}</button>
        ))}
      </div>
      {tab === "campaigns" && <CampaignsTab courses={courses} />}
      {tab === "automation" && <AutomationTab courses={courses} sources={meta?.paramSources || []} />}
      {tab === "demos" && <DemosTab courses={courses} />}
    </div>
  );
};

export default LeadSetup;
